import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api.js';
import { useAuth } from '../../auth.jsx';
import { useI18n } from '../../i18n/index.jsx';
import { Alert } from '../../components/ui.jsx';
import Icon from '../../components/Icon.jsx';
import { isStaff } from '../account/AccountLayout.jsx';
import { StatusBadge, fmtDate } from './ws.jsx';
import TutorialSlot from '../../tutorials/TutorialSlot.jsx';

// "Mon espace": my customers, new messages, quote requests assigned to me,
// tasks due today, documents waiting for an answer, notifications, my
// recent activity.
export default function WorkspaceHome() {
  const { t, lang, errorText } = useI18n();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [notes, setNotes] = useState([]);
  const [error, setError] = useState('');
  const load = useCallback(() => {
    api('/workspace/dashboard').then(setD).catch((e) => setError(errorText(e)));
    api('/workspace/notifications').then(setNotes).catch(() => {});
  }, [errorText]);
  useEffect(() => { load(); }, [load]);
  async function done(task) { await api(`/workspace/tasks/${task.id}`, { method: 'PATCH', body: { done: true } }).catch(() => {}); load(); }
  async function readAll() { await api('/workspace/notifications/read', { method: 'POST' }).catch(() => {}); load(); }
  const staff = isStaff(user);
  const unread = notes.filter((n) => !n.read).length;
  const stats = d ? [
    ['customers', d.customers, '/account/workspace/customers'],
    ['followupsDue', d.followupsDue, '/account/workspace/customers?due=1'],
    ['messages', d.messages, staff ? '/account/workspace/messages' : '/account/messages'],
    ['requests', d.requests, staff ? '/account/workspace/requests' : '/account/requests'],
  ] : [];
  return (
    <div className="ws-page">
      <div className="acc-head"><TutorialSlot place="ws.home" />
        <div>
          <h1>{t('ws.home.title')}</h1>
          <p className="muted">{t('ws.home.lead', { name: user.person?.firstName || user.name, company: user.companyName })}</p>
        </div>
      </div>
      <Alert>{error}</Alert>
      {d && (
        <>
          <div className="acc-stats">
            {stats.map(([k, n, to]) => <Link key={k} to={to} className="acc-stat"><strong>{n}</strong><span>{t(`ws.home.stat.${k}`)}</span></Link>)}
          </div>
          <div className="ws-grid">
            <section className="ws-card">
              <h2><Icon name="calendar" size={18} /> {t('ws.home.tasksToday')}</h2>
              <ul className="ws-list">
                {d.tasks.map((x) => (
                  <li key={x.id}>
                    <input type="checkbox" onChange={() => done(x)} aria-label={t('ws.tasks.done')} />
                    <span className="ws-grow"><span className="ws-ellipsis">{x.title}</span>{x.customer && <span className="muted small"> — {x.customer}</span>}</span>
                    <span className={`small${x.dueOn && x.dueOn < new Date().toISOString().slice(0, 10) ? ' field-error' : ' muted'}`}>{fmtDate(x.dueOn, lang)}</span>
                  </li>
                ))}
                {!d.tasks.length && <li className="muted small">{t('ws.home.noTasks')}</li>}
              </ul>
              <Link to="/account/workspace/tasks" className="small">{t('ws.home.allTasks')} →</Link>
            </section>
            <section className="ws-card">
              <h2><Icon name="file" size={18} /> {t('ws.home.docs')}</h2>
              <ul className="ws-list">
                {d.documents.map((x) => (
                  <li key={x.id}>
                    <Link to={`/account/workspace/documents/${x.id}`} className="ws-grow ws-ellipsis">{x.ref} — {x.customer}</Link>
                    <StatusBadge kind="doc" value={x.status} />
                  </li>
                ))}
                {!d.documents.length && <li className="muted small">{t('ws.home.noDocs')}</li>}
              </ul>
            </section>
            <section className="ws-card">
              <div className="ws-row"><h2 className="ws-grow"><span className="ws-bell"><Icon name="bell" size={18} />{unread > 0 && <span className="ws-bell-n">{unread}</span>}</span> {t('ws.home.notifications')}</h2>
                {unread > 0 && <button type="button" className="btn btn-link btn-sm" onClick={readAll}>{t('ws.home.markRead')}</button>}</div>
              <ul className="ws-list">
                {notes.slice(0, 8).map((n) => (
                  <li key={n.id} className={n.read ? 'muted' : ''}>
                    {n.link ? <Link to={n.link} className="ws-grow ws-ellipsis">{!n.read && '● '}{n.title}</Link> : <span className="ws-grow">{n.title}</span>}
                    <span className="small muted">{fmtDate(n.at, lang)}</span>
                  </li>
                ))}
                {!notes.length && <li className="muted small">{t('ws.home.noNotifications')}</li>}
              </ul>
            </section>
            <section className="ws-card">
              <h2><Icon name="history" size={18} /> {t('ws.home.activity')}</h2>
              <ul className="ws-list">
                {d.activity.map((a, i) => <li key={i} className="small"><span className="ws-grow ws-ellipsis">{a.details || a.action}</span><span className="muted">{fmtDate(a.at, lang, true)}</span></li>)}
                {!d.activity.length && <li className="muted small">{t('ws.home.noActivity')}</li>}
              </ul>
            </section>
          </div>
        </>
      )}
    </div>
  );
}
